import React, { useEffect, useState, useCallback, useMemo }from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from "react-router-dom";

import T, { Translate } from '../../i18n/i18n';
import { TableList, Title, ButtonIcon, HeaderRow, ButtonBack, Row, PaginatorRow, FilterPresentation, NoItemsFilter, NoItems } from './FindTables.styles';
import { findTables } from '../../../services/tables';
import TableItemFull from '../TableItem/TableItemFull';
import Paginator from '../../Forms/Paginator/Paginator';
import FormFilter from './FormFilter/FormFilter';


export default () => {
    const [highlightTables, setHighlightTables] = useState([]);
    const [commonTables, setCommonTables] = useState([]);
    const [numCommonTables, setNumCommonTables] = useState(0);
    const [page, setPage] = useState(1);
    const [openFilter, setOpenFilter] = useState(false);
    const [filterData, setFilterData] = useState({});
    
    const history = useHistory();

    const changePage = useCallback((p) => {
        setPage(p);
        updateTables(filterData, p);
    }, [filterData]);

    const filterItems = useMemo(() => {
        const result = [];

        const printArray = (arr, joinBoolean = "and") => {
            let result = "";

            for(let i=0; i<arr.length; i++) {
                if (i === 0) {
                    result += arr[i];
                } else if (i !== arr.length-1) {
                    result += ', ' + arr[i];
                } else {
                    result += " " + Translate(joinBoolean) + arr[i];
                }
            }

            return result;
        }

        const GetFilterDescription = (item) => {
            return `${Translate("filter."+item.key)} (${typeof item.value === 'string' ? item.value : printArray(item.value, "or")})`;
        }
        
        if (filterData && filterData.name) {
            result.push({key:'name', value: filterData.name});
        }
        if (filterData && filterData.frequency && filterData.frequency !== 'any') {
            result.push({key:'frequency', value: Translate(`filter.frequency-${filterData.frequency}`)});
        }
        if (filterData && filterData.calendar === 'auto') {
            result.push({key:'calendar', value: Translate('filter.calendar-auto')});
        }
        if (filterData && filterData.calendar === 'manual') {
            result.push({key:'calendar', value: Translate('filter.calendar-manual')});
            if (filterData.days !== 0) {
                let days = (new Array(7)).fill(0)
                        .map((_,i)=>2**(6-i))
                        .filter(i=>filterData.days & i)
                        .map(i=>Translate("date-d."+(Math.log2(i))))
                        .reverse();
                result.push({key:'days', value: days});
            }
            
            if (filterData.timeFrom && filterData.timeTo) {
                result.push({key:'times', value: Translate("tabledetail.time-value").replace("$1", filterData.timeFrom).replace("$2", filterData.timeTo)});
            }

        }

        if (filterData.tagSystems && filterData.tagSystems.length) {
            result.push({key:'gamesystems', value: filterData.tagSystems.map(i=>i[Translate("lang")])});
        }
        
        if (filterData.tagContent && filterData.tagContent.length) {
            result.push({key:'contentInclude', value: filterData.tagContent.map(i=>i[Translate("lang")])});
        }
        
        if (filterData.tagContentAvoid && filterData.tagContentAvoid.length) {
            result.push({key:'contentAvoid', value: filterData.tagContentAvoid.map(i=>i[Translate("lang")])});
        }

        if (filterData.tagTools && filterData.tagTools.length) {
            result.push({key:'tools', value: filterData.tagTools.map(i=>i[Translate("lang")])});
        }
        
        if (result.length > 0) {
            return printArray(result.map(r => GetFilterDescription(r)));
        }

        return null;

    }, [filterData]);

    const updateTables = (data, page) => {
        let setState = true;

        (async () => {
            let tables = await findTables(data ?? {}, page ? (page-1) : 0);
            if (setState && tables.status === 'ok') {
                setHighlightTables(tables.payload.highlight);
                setCommonTables(tables.payload.common);
                setNumCommonTables(tables.payload.numTables);
            } else {
                setCommonTables([]);
                setNumCommonTables(0);
            }
        })();

        return () => {
            setState = false;
        }
    };

    const handleBack = () => {
        history.goBack();
    };

    const handleRefresh = () => {
        updateTables(filterData, page);
    };

    const handleOpenFilter = () => {
        setOpenFilter(true);
    };

    const onFilter = (data) => {
        setFilterData(data);
        setPage(1);
        updateTables(data, 1);
    }

    const openTable = (id) => {
        history.push(`/home/table/${id}`);
    }

    const numPages = useMemo(() => {
        return Math.ceil(numCommonTables/10);
    }, [numCommonTables]);

    useEffect(() => {
        const fn = updateTables();
        return () => {fn();}
    }, []);


    return <>
        <HeaderRow>
            <ButtonBack color="#aaa" onClick={handleBack} />
            <Title><T value="findtables.title" /></Title>
        </HeaderRow>
        {highlightTables && highlightTables.length ? <>
            <Row className='noTopDistance'>
                <Title><T value="findtables.titleHighlight" /></Title>
            </Row>
            <TableList>
                { highlightTables.map(table => <TableItemFull className='half' key={table.id} table={table} onOpen={()=>{openTable(table.id)}}/>) }
            </TableList>
        </>: null }
        <Row>
            <Title><T value="findtables.titleCommon" /></Title>
            <ButtonIcon color="#69f" onClick={handleOpenFilter} icon="\F0349" title={Translate("findtables.filter")} />
            <ButtonIcon color="#f92" onClick={handleRefresh} icon="\F0450" title={Translate("tables.refresh")} />
        </Row>
        {filterItems ? <FilterPresentation><T value="filter.filteringBy" />{filterItems}</FilterPresentation> : null}
        <TableList>
            {commonTables && commonTables.length ? 
                commonTables.map(table => <TableItemFull className='half' key={table.id} table={table} onOpen={()=>{openTable(table.id)}}/>)
            : null }
            {commonTables.length === 0 ? (
                filterItems ? 
                    <NoItemsFilter><T value="filter.noItemsFilter" /></NoItemsFilter> 
                :
                    <NoItems><T value="filter.noItems" /></NoItems>
            ) : null }
        </TableList>
        <PaginatorRow>
            <Paginator 
                current={page} 
                pages={numPages}
                onPageChange={changePage}
            />
        </PaginatorRow>

        <FormFilter 
            opened={openFilter} 
            onFilter={onFilter}
            onClose={()=>{setOpenFilter(false)}}
        />
    </>
};